import {
    CircleLayerSpecification,
    ExpressionSpecification,
    FillExtrusionLayerSpecification,
    FillLayerSpecification,
    Layer,
    LineLayerSpecification,
    Map,
    StyleSpecification,
    SymbolLayerSpecification,
    VectorSourceSpecification,
} from "@iventis/mapbox-gl";
import { v4 as uuid } from "uuid";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { LineJoin } from "@iventis/domain-model/model/lineJoin";
import { LineEnd } from "@iventis/domain-model/model/lineEnd";
import { MultiPolygon, Polygon } from "geojson";
import { IconPlacement } from "@iventis/domain-model/model/iconPlacement";
import { LineTerminatorIcon } from "@iventis/domain-model/model/lineTerminatorIcon";
import { AreaDimension } from "@iventis/domain-model/model/areaDimension";
import { StyleValue } from "@iventis/domain-model/model/styleValue";
import { StyleValueExtractionMethod } from "@iventis/domain-model/model/styleValueExtractionMethod";
import { ZoomableValue } from "@iventis/domain-model/model/zoomableValue";
import { ZoomableValueExtractionMethod } from "@iventis/domain-model/model/zoomableValueExtractionMethod";
import { PointStyle } from "@iventis/domain-model/model/pointStyle";
import { AreaStyle } from "@iventis/domain-model/model/areaStyle";
import { LineStyle } from "@iventis/domain-model/model/lineStyle";
import { ZoomValue } from "@iventis/domain-model/model/zoomValue";
import { IconOrientation } from "@iventis/domain-model/model/iconOrientation";
import { LineType } from "@iventis/domain-model/model/lineType";
import { TextPosition } from "@iventis/domain-model/model/textPosition";
import { IconStyle } from "@iventis/domain-model/model/iconStyle";
import { DataField } from "@iventis/domain-model/model/dataField";
import bbox from "@turf/bbox";
import {
    feetInMile,
    feetToMeter,
    metersInKilometer,
    metersSquaredInKiloMeterSquared,
    mileToMeter,
    smallestKilometerValueToShow,
    smallestMilesValueToShow,
    squaredFeetInSquaredMile,
    squareFeetToSquareMeter,
    squareMeterToSquareMile,
} from "@iventis/utilities/src/unit-conversion/constants";
import { UnitOfMeasurement } from "@iventis/domain-model/model/unitOfMeasurement";
import { IconAlignment } from "@iventis/domain-model/model/iconAlignment";
import { createStaticStyleValue, getStaticStyleValue, getStaticStyleValueFromMapped } from "@iventis/layer-style-helpers";
import { getMapObjectAreaSystemDataField, getMapObjectLengthSystemDataField } from "@iventis/datafields";
import { DataFieldListItem } from "@iventis/domain-model/model/dataFieldListItem";
import { getDefaultStyleProperty, getStaticAndMappedValues, getTextStyle } from "../../utilities/style-helpers";
import { BasicMapLayer, LayerStorageScope, MapModuleLayer, MapState, TileSource } from "../../types/store-schema";
import { getCentroidLayerID, removeLayerIdSuffixes } from "../engine-generic";
import {
    AnySupportedMapboxLayer,
    ApplyListItemTextFunction,
    automaticTextAnchor,
    boldFontStack,
    ExportOptions,
    iventisToMapboxTextStyleProperty,
    LineEndString,
    LineJoinString,
    MapboxInitialPosition,
    regularFontStack,
    transparentColour,
} from "./engine-mapbox-types-and-constants";
import { AggregateLayers, MapboxlayerWithSublayerType, SubLayerType } from "./sublayer-types";
import { AssetOptions, EngineInterpreterOptions, StylePropertyToValueMap } from "../../types/internal";
import { DISTANCE_MARKER_LABEL_PROPERTY } from "../../utilities/distance-marker-helpers";
import {
    MAPBOX_MAX_ZOOM,
    MAPBOX_MIN_ZOOM,
    PredefinedZoomLevels,
    localSuffix,
    triangleSprintName,
    lineTerminalIconSpriteName,
    MAPBOX_FALLBACK_ZOOM,
    IVENTIS_MAP_BACKGROUND_SLOT_ID,
    IVENTIS_SITEMAP_TEXT_SLOT_ID,
    IVENTIS_SITEMAP_LINE_SLOT_ID,
    IVENTIS_SITEMAP_AREA_SLOT_ID,
    IVENTIS_SITEMAP_RASTER_SLOT_ID,
    lineDistanceMarkerSpriteName,
} from "../constants";
import { iconRotationValueToMapboxStyleValue } from "./mapbox-icon-style-helpers";

type BBox2d = [number, number, number, number];

type LineDistanceMarkerPadding = [number, number, number, number];

const lineDistanceMarkerSourceSuffix = "distance-marker";

/**
 * Identifies local GeoJSON layers changed by a store update.
 *
 * The reducer preserves references for untouched layer arrays, allowing the Mapbox engine to
 * replay only geometry received before the map loaded rather than all local geometry.
 *
 * @param currentGeoJson Local GeoJSON after the store update.
 * @param previousGeoJson Local GeoJSON before the store update.
 * @returns Layer IDs whose local GeoJSON was added, removed, or replaced.
 */
export function getChangedLocalGeoJsonLayerIds(currentGeoJson: MapState["geoJSON"]["value"], previousGeoJson: MapState["geoJSON"]["value"]): string[] {
    const layerIds = new Set([...Object.keys(currentGeoJson), ...Object.keys(previousGeoJson)]);
    return [...layerIds].filter((layerId) => currentGeoJson[layerId] !== previousGeoJson[layerId]);
}

export function getLineDistanceMarkerSourceId(lineLayerId: string) {
    return `${lineLayerId}_${lineDistanceMarkerSourceSuffix}`;
}

function generateLineDistanceMarkerSubLayerSource(layer: MapModuleLayer) {
    const isLayerLocal = layer.storageScope === LayerStorageScope.LocalOnly || layer.id.includes(`_${localSuffix}`);
    return isLayerLocal ? getLineDistanceMarkerSourceId(layer.id) : layer.source;
}

export function iventisSourceToMapboxSource(iventisSource: TileSource, bounds?: BBox2d): VectorSourceSpecification {
    const mbxSourceValue: VectorSourceSpecification = {
        type: "vector",
    };

    if (iventisSource.tiles !== undefined) {
        mbxSourceValue.tiles = iventisSource.tiles;
    }

    if (iventisSource.url !== undefined) {
        mbxSourceValue.url = iventisSource.url;
    }

    if (bounds != null) {
        mbxSourceValue.bounds = bounds;
    }

    return mbxSourceValue;
}

export const iventisLayerToHighlightBaseLayer = (
    iventisLayer: MapModuleLayer,
    preview: boolean,
    id = uuid(),
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"],
    projectDatafields?: DataField[]
): AnySupportedMapboxLayer => {
    switch (iventisLayer.styleType) {
        case StyleType.Line:
            return iventisLineToBaseSublayer(iventisLayer, id, filter);
        case StyleType.Area:
            return iventisAreaToBaseSublayer(iventisLayer, id, filter);
        case StyleType.Point:
            return iventisPointToBaseSublayer(iventisLayer, id, filter);
        case StyleType.Icon:
            return iventisIconToBaseSublayer(
                iventisLayer,
                preview,
                {},
                { projectDataFields: projectDatafields, unitOfMeasurement: UnitOfMeasurement.Metric },
                id,
                filter,
                applyTextFieldExpression,
                getAttrbuteListItems
            );
        case StyleType.LineModel:
        case StyleType.Model:
        default:
            throw new Error("Layer type is not implemented");
    }
};

// parse an Iventis area layer to a mapbox fill layer
export function iventisAreaToAggregateLayer(
    iventisArea: MapModuleLayer,
    preview: boolean,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"]
): MapboxlayerWithSublayerType[] {
    const aggregatedLayers: MapboxlayerWithSublayerType[] = [];

    if (getStaticStyleValue(iventisArea.areaStyle.dimension) === AreaDimension.Three) {
        const extrusion = iventis3DAreaToBaseSublayer(iventisArea, iventisArea.id, filter);
        aggregatedLayers.push({ type: SubLayerType.EXTRUSION, id: extrusion.id, layer: extrusion, styleType: StyleType.Area });
    } else {
        // Only include fill and outline if we're on 2D area layers.
        if (getStaticStyleValue(iventisArea.areaStyle.fill)) {
            const base = iventisAreaToBaseSublayer(iventisArea, iventisArea.id, filter);
            aggregatedLayers.push({ type: SubLayerType.BASE, id: base.id, layer: base, styleType: StyleType.Area });
        }
        if (getStaticStyleValue(iventisArea.areaStyle.outline)) {
            const outlineLayer = createOutlineSubLayer(iventisArea, iventisArea.source, iventisArea.storageScope, iventisArea.areaStyle, iventisArea.visible, filter);
            aggregatedLayers.push({ type: SubLayerType.POLYGON_OUTLINE, id: outlineLayer.id, layer: outlineLayer, styleType: StyleType.Area });
        }
    }

    if (getStaticStyleValue(iventisArea.areaStyle.text)) {
        const textLayer = createTextSublayer(iventisArea, iventisArea.areaStyle, preview, lengthAndArea, filter, applyTextFieldExpression, getAttrbuteListItems);
        aggregatedLayers.push({ type: SubLayerType.POLYGON_TEXT, id: textLayer.id, layer: textLayer, styleType: StyleType.Area });
    }

    // if the layer is remote
    if (!iventisArea.id.includes(`_${localSuffix}`) && iventisArea.remote) {
        // set source layer id as base layer id
        aggregatedLayers.forEach(({ layer, type }) => {
            if (type === SubLayerType.POLYGON_TEXT) {
                // eslint-disable-next-line no-param-reassign
                layer["source-layer"] = removeLayerIdSuffixes(getCentroidLayerID(iventisArea.id));
            } else {
                // eslint-disable-next-line no-param-reassign
                layer["source-layer"] = removeLayerIdSuffixes(iventisArea.id);
            }
        });
    }
    return aggregatedLayers;
}

export function iventisPointToAggregateLayer(
    iventisLayer: MapModuleLayer,
    preview: boolean,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"]
): MapboxlayerWithSublayerType[] {
    const aggregatedLayers: MapboxlayerWithSublayerType[] = [];
    const base: CircleLayerSpecification = iventisPointToBaseSublayer(iventisLayer, iventisLayer.id, filter);

    aggregatedLayers.push({ type: SubLayerType.BASE, id: base.id, layer: base, styleType: StyleType.Point });

    if (getStaticStyleValue(iventisLayer.pointStyle.text)) {
        const textLayer = createTextSublayer(iventisLayer, iventisLayer.pointStyle, preview, lengthAndArea, filter, applyTextFieldExpression, getAttrbuteListItems);
        aggregatedLayers.push({ type: SubLayerType.POINT_TEXT, id: textLayer.id, layer: textLayer, styleType: StyleType.Point });
    }

    return aggregatedLayers;
}

export function iventisIconToAggregateLayer(
    iventisLayer: MapModuleLayer,
    preview: boolean,
    sdfIconIds: { [key: string]: string },
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"]
): MapboxlayerWithSublayerType[] {
    const aggregatedLayers: MapboxlayerWithSublayerType[] = [];

    const base: SymbolLayerSpecification = iventisIconToBaseSublayer(
        iventisLayer,
        preview,
        sdfIconIds,
        lengthAndArea,
        iventisLayer.id,
        filter,
        applyTextFieldExpression,
        getAttrbuteListItems
    );

    aggregatedLayers.push({ type: SubLayerType.BASE, id: base.id, layer: base, styleType: StyleType.Icon });

    return aggregatedLayers;
}

export function createTextSublayer<Style extends AreaStyle | PointStyle | LineStyle>(
    layer: MapModuleLayer,
    style: Style,
    preview: boolean,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"]
) {
    const { id, source, storageScope, visible } = layer;
    const textLayerId = uuid();

    let textContentValue = style.textContent;
    if (textContentValue == null) {
        textContentValue = getDefaultStyleProperty(style.styleType, "textContent");
    }
    const textField = textContentValueToMapboxStyleValue(textContentValue, lengthAndArea, textLayerId, layer.id, applyTextFieldExpression, getAttrbuteListItems);
    const textLayer: SymbolLayerSpecification = {
        id: textLayerId,
        type: "symbol",
        paint: {
            "text-color": getStaticStyleValue(style.textColour ?? getDefaultStyleProperty(style.styleType, "textColour")),
            "text-halo-color": getStaticStyleValue(style.textOutlineColour ?? getDefaultStyleProperty(style.styleType, "textOutlineColour")),
            "text-halo-width": getStaticStyleValue(style.textOutlineWidth ?? getDefaultStyleProperty(style.styleType, "textOutlineWidth")),
            "text-opacity": getStaticStyleValue(style.textOpacity ?? getDefaultStyleProperty(style.styleType, "textOpacity")),
        },
        layout: {
            visibility: visible ? "visible" : "none",
            "text-allow-overlap": getStaticStyleValue(style.textOverlap ?? getDefaultStyleProperty(style.styleType, "textOverlap")),
            "text-anchor": "center",
            "text-field": textField,
            "text-size": styleValueToMapboxStyleValue(style.textSize ?? getDefaultStyleProperty(style.styleType, "textSize")),
            "text-font": getStaticStyleValue(style.textBold ?? getDefaultStyleProperty(style.styleType, "textBold")) ? boldFontStack : regularFontStack,
        },
        source: generateTextSubLayerSource(id, source, style.styleType, storageScope),
        filter,
        metadata: {
            type: "text",
            name: layer.name,
        },
    };

    if (style.styleType === StyleType.Point) {
        textLayer.layout = {
            ...textLayer.layout,
            "text-variable-anchor": styleValueParser.textPosition(getStaticStyleValue(style.textPosition ?? getDefaultStyleProperty(style.styleType, "textPosition")), preview),
            "text-radial-offset": styleValueParser.textOffset(
                getStaticStyleValue(style.textOffset ?? getDefaultStyleProperty(style.styleType, "textOffset")),
                style.textSize ?? getDefaultStyleProperty(style.styleType, "textSize")
            ),
        };
    }

    if (style.styleType === StyleType.Area && getStaticStyleValue(style.dimension) === AreaDimension.Three) {
        // Raise the text to the top of the extrusion so it sits in the centre of the 3D area
        textLayer.paint = {
            ...textLayer.paint,
            "symbol-z-offset": styleValueToMapboxStyleValue(
                style.height ?? getDefaultStyleProperty(style.styleType, "height")
            ) as SymbolLayerSpecification["paint"]["symbol-z-offset"],
        };

        textLayer.layout = {
            ...textLayer.layout,
            "symbol-z-elevate": true,
            "symbol-elevation-reference": "ground",
        };
    }

    if (style.styleType === StyleType.Line) {
        textLayer.paint = {
            ...textLayer.paint,
            "text-translate": [getStaticStyleValue(style.offset ?? getDefaultStyleProperty(style.styleType, "offset")), 0],
        };

        textLayer.layout = {
            ...textLayer.layout,
            "text-anchor": "bottom",
            "symbol-placement": "line",
        };
    }

    if (storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(textLayer, id, removeLayerIdSuffixes(style.styleType === StyleType.Area ? getCentroidLayerID(id) : id));
    }

    return textLayer;
}

function generateTextSubLayerSource(id: string, source: string, styleType: StyleType, storageScope: LayerStorageScope) {
    if (styleType === StyleType.Area) {
        const isLayerLocal = id.includes(`_${localSuffix}`) || storageScope === LayerStorageScope.LocalOnly;
        return isLayerLocal ? getCentroidLayerID(id) : source;
    }

    return source;
}

export function createOutlineSubLayer<Style extends AreaStyle | LineStyle>(
    iventisLine: MapModuleLayer,
    source: string,
    storageScope: LayerStorageScope,
    style: Style,
    visible: boolean,
    filter: Layer["filter"]
) {
    const outlineLayer: LineLayerSpecification = {
        id: uuid(),
        type: "line",
        layout: {
            visibility: visible ? "visible" : "none",
        },
        paint: {
            "line-color": styleValueToMapboxStyleValue(style.outlineColour ?? getDefaultStyleProperty(style.styleType, "outlineColour")),
            "line-blur": getStaticStyleValue(style.outlineBlur ?? getDefaultStyleProperty(style.styleType, "outlineBlur")),
            "line-opacity": getStaticStyleValue(style.outlineOpacity ?? getDefaultStyleProperty(style.styleType, "outlineOpacity")),
        },
        metadata: {
            name: iventisLine.name,
            type: "outline",
        },
        source,
    };

    if (style.styleType === StyleType.Area) {
        const outlineWidth = getStaticStyleValue(style.outlineWidth ?? getDefaultStyleProperty(style.styleType, "outlineWidth"));
        outlineLayer.paint = {
            ...outlineLayer.paint,
            // Ensure the outline is not overlapping the polygon
            "line-offset": calculateOffsetForPolygonOutline(outlineWidth),
            "line-width": outlineWidth,
        };
    }

    if (style.styleType === StyleType.Line) {
        const outlineWidth = calculateLineWidthForLineOutline(style.width, style.outlineWidth ?? getDefaultStyleProperty(style.styleType, "outlineWidth"));
        outlineLayer.paint = {
            ...outlineLayer.paint,
            "line-width": outlineWidth,
            "line-offset": getStaticStyleValue(style.offset ?? getDefaultStyleProperty(style.styleType, "offset")),
        };

        outlineLayer.layout = {
            ...outlineLayer.layout,
            "line-cap": "round",
            "line-join": "round",
        };
    }

    if (filter != null && filter.length !== 0) {
        outlineLayer.filter = filter;
    }

    if (storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(outlineLayer, iventisLine.id);
    }

    return outlineLayer;
}

// parse an Iventis line layer to a mapbox line layer
export function iventisLineToAggregateLayer(
    iventisLine: MapModuleLayer,
    preview: boolean,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttrbuteListItems?: EngineInterpreterOptions["getAttributeListItems"]
): MapboxlayerWithSublayerType[] {
    const aggregatedLayers: MapboxlayerWithSublayerType[] = [];

    if (getStaticStyleValue(iventisLine.lineStyle.outline)) {
        const outlineLayer = createOutlineSubLayer(iventisLine, iventisLine.source, iventisLine.storageScope, iventisLine.lineStyle, iventisLine.visible, filter);
        aggregatedLayers.push({ type: SubLayerType.LINE_OUTLINE, id: outlineLayer.id, layer: outlineLayer, styleType: StyleType.Line });
    }

    const base: LineLayerSpecification = iventisLineToBaseSublayer(iventisLine, iventisLine.id, filter);

    aggregatedLayers.push({ type: SubLayerType.BASE, id: base.id, layer: base, styleType: StyleType.Line });

    if (getStaticStyleValue(iventisLine.lineStyle.arrows)) {
        const arrowLayer = createArrowsSublayer(iventisLine, filter);
        aggregatedLayers.push({ type: SubLayerType.LINE_ICON, id: arrowLayer.id, layer: arrowLayer, styleType: StyleType.Line });
    }

    const distanceMarkersEnabled = getStaticStyleValue(iventisLine.lineStyle.distanceMarker ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "distanceMarker"));
    const isLocalLine = iventisLine.storageScope === LayerStorageScope.LocalOnly || iventisLine.id.includes(`_${localSuffix}`);
    if (distanceMarkersEnabled && isLocalLine) {
        const distanceMarkerLayer = createLineDistanceMarkerSublayer(iventisLine, filter, preview);
        aggregatedLayers.push({ type: SubLayerType.LINE_DISTANCE_MARKER, id: distanceMarkerLayer.id, layer: distanceMarkerLayer, styleType: StyleType.Line });
    }

    const terminatorIconValue = getStaticStyleValue(iventisLine.lineStyle.lineTerminatorIcon ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "lineTerminatorIcon"));
    if (terminatorIconValue === LineTerminatorIcon.Start || terminatorIconValue === LineTerminatorIcon.Both) {
        const startLayer = createLineTerminatorSublayer(iventisLine, filter, "line-start");
        aggregatedLayers.push({ type: SubLayerType.LINE_TERMINATOR_START, id: startLayer.id, layer: startLayer, styleType: StyleType.Line });
    }
    if (terminatorIconValue === LineTerminatorIcon.End || terminatorIconValue === LineTerminatorIcon.Both) {
        const endLayer = createLineTerminatorSublayer(iventisLine, filter, "line-end");
        aggregatedLayers.push({ type: SubLayerType.LINE_TERMINATOR_END, id: endLayer.id, layer: endLayer, styleType: StyleType.Line });
    }

    if (getStaticStyleValue(iventisLine.lineStyle.text)) {
        const textLayer = createTextSublayer(iventisLine, iventisLine.lineStyle, preview, lengthAndArea, filter, applyTextFieldExpression, getAttrbuteListItems);
        aggregatedLayers.push({ type: SubLayerType.LINE_TEXT, id: textLayer.id, layer: textLayer, styleType: StyleType.Line });
    }

    return aggregatedLayers;
}

export function createArrowsSublayer(iventisLine: MapModuleLayer, filter: Layer["filter"]) {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisLine.lineStyle.width.staticValue);
    const arrowLayer: SymbolLayerSpecification = {
        id: uuid(),
        type: "symbol",
        paint: {
            "icon-color": styleValueToMapboxStyleValue(iventisLine.lineStyle.arrowColour ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "arrowColour")),
            "icon-opacity": getStaticStyleValue(iventisLine.lineStyle.arrowOpacity ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "arrowOpacity")),
        },
        layout: {
            visibility: iventisLine.visible ? "visible" : "none",
            "icon-offset": [getStaticStyleValue(iventisLine.lineStyle.offset ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "offset")), 0],
            "icon-image": triangleSprintName,
            "symbol-spacing": styleValueParser.symbolSpacing(
                getStaticStyleValue(iventisLine.lineStyle.arrowSpacing ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "arrowSpacing"))
            ),
            "icon-rotation-alignment": "map",
            "icon-allow-overlap": true,
            "icon-rotate": 90,
            "symbol-placement": styleValueParser.iconPlacement(
                getStaticStyleValue(iventisLine.lineStyle.iconPlacement ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "iconPlacement"))
            ),
            "icon-size": getStaticStyleValue(iventisLine.lineStyle.arrowSize ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "arrowSize")),
            "symbol-sort-key": styleValueToMapboxStyleValue(iventisLine.lineStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        filter,
        ...addMaxMinZoomLevels(maxZoom, minZoom),
        source: iventisLine.source,
        metadata: {
            name: iventisLine.name,
            type: "arrows",
        },
    };

    if (iventisLine.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(arrowLayer, iventisLine.id);
    }

    return arrowLayer;
}

export function createLineTerminatorSublayer(iventisLine: MapModuleLayer, filter: Layer["filter"], placement: "line-start" | "line-end") {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisLine.lineStyle.width.staticValue);
    const lineTerminatorIconSizeByZoom: ExpressionSpecification = [
        "interpolate",
        ["linear"],
        ["zoom"],
        PredefinedZoomLevels.world,
        0.4,
        PredefinedZoomLevels.country,
        0.5,
        PredefinedZoomLevels.city,
        0.6,
        PredefinedZoomLevels.street,
        0.6,
        PredefinedZoomLevels.max,
        1.2,
    ];
    const terminatorLayer: SymbolLayerSpecification = {
        id: uuid(),
        type: "symbol",
        paint: {
            "icon-color": styleValueToMapboxStyleValue(iventisLine.lineStyle.colour),
            "icon-opacity": getStaticStyleValue(iventisLine.lineStyle.opacity ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "opacity")),
        },
        layout: {
            visibility: iventisLine.visible ? "visible" : "none",
            "icon-offset": [getStaticStyleValue(iventisLine.lineStyle.offset ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "offset")), 0],
            "icon-rotate": 90,
            "icon-image": lineTerminalIconSpriteName,
            "icon-rotation-alignment": "map",
            "icon-allow-overlap": true,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            "symbol-placement": placement as any,
            "icon-size": lineTerminatorIconSizeByZoom,
            "symbol-sort-key": styleValueToMapboxStyleValue(iventisLine.lineStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        filter,
        ...addMaxMinZoomLevels(maxZoom, minZoom),
        source: iventisLine.source,
        metadata: {
            name: iventisLine.name,
            type: placement === "line-start" ? "line-terminator-start" : "line-terminator-end",
        },
    };

    if (iventisLine.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(terminatorLayer, iventisLine.id);
    }

    return terminatorLayer;
}

/** Text size the distance marker padding is designed for, padding scales up or down from this as the text size changes */
const scaleLineDistanceMarkerPadding = (padding: LineDistanceMarkerPadding, textSize: number) => padding.map((value) => (value * textSize) / 16);

/**
 * Padding between the distance marker text and the edge of its bubble, scaled with the text size so larger text gets a larger bubble.
 * Text size can vary by zoom level so the padding is returned as a matching zoom expression when it does.
 * The preview uses a single text size so its padding is never scaled.
 */
export function getLineDistanceMarkerTextFitPadding(textSize: StyleValue<number>, preview = false): ExpressionSpecification | number[] {
    if (preview) {
        // The preview marker uses a narrower padding as it is shown at a fixed, smaller text size
        return [8, 12, 8, 12];
    }

    const basePadding: LineDistanceMarkerPadding = [8, 16, 8, 16];

    if (textSize?.extractionMethod !== StyleValueExtractionMethod.Static || textSize.staticValue == null) {
        return basePadding;
    }

    const zoomableTextSize = textSize.staticValue;
    const hasMappedZoomValues = zoomableTextSize.mappedZoomValues != null && Object.keys(zoomableTextSize.mappedZoomValues).length > 0;

    if (zoomableTextSize.extractionMethod === ZoomableValueExtractionMethod.Static || !hasMappedZoomValues) {
        return scaleLineDistanceMarkerPadding(basePadding, zoomableTextSize.staticValue);
    }

    const zoomStops = Object.entries(zoomableTextSize.mappedZoomValues).flatMap(([zoomLevel, zoomValue]) => [
        Number.parseFloat(zoomLevel),
        ["literal", scaleLineDistanceMarkerPadding(basePadding, zoomValue.hidden ? 0 : zoomValue.value)],
    ]);

    return (zoomableTextSize.extractionMethod === ZoomableValueExtractionMethod.Continuous
        ? ["interpolate", ["linear"], ["zoom"], ...zoomStops]
        : ["step", ["zoom"], ["literal", scaleLineDistanceMarkerPadding(basePadding, zoomableTextSize.staticValue)], ...zoomStops]) as ExpressionSpecification;
}

export function createLineDistanceMarkerSublayer(iventisLine: MapModuleLayer, filter: Layer["filter"], preview = false) {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisLine.lineStyle.width.staticValue);

    const distanceMarkerUnit = getStaticStyleValue(iventisLine.lineStyle.distanceMarkerUnit ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "distanceMarkerUnit"));
    const distanceMarkerTextSize = iventisLine.lineStyle.distanceMarkerTextSize ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "distanceMarkerTextSize");

    const distanceMarkerLayer: SymbolLayerSpecification = {
        id: uuid(),
        type: "symbol",
        paint: {
            "text-color": "#FFFFFF",
            // Lift the bubble and its label clear of the line the marker sits on
            "icon-translate": [0, -7],
            "icon-translate-anchor": "viewport",
            "text-translate": [0, -8],
            "text-translate-anchor": "viewport",
        },
        layout: {
            "text-field": ["get", DISTANCE_MARKER_LABEL_PROPERTY],
            // The preview is shown at a fixed size so the marker text ignores the layer's text size and uses a single size
            "text-size": preview ? 12 : styleValueToMapboxStyleValue(distanceMarkerTextSize),
            visibility: iventisLine.visible ? "visible" : "none",
            "icon-image": lineDistanceMarkerSpriteName,
            "icon-size": 0.7,
            "icon-anchor": "center",
            "icon-text-fit-padding": getLineDistanceMarkerTextFitPadding(distanceMarkerTextSize, preview),
            "icon-text-fit": "both",
            "symbol-placement": "point",
            // Centre the label vertically within the bubble's rectangular body.
            "text-offset": [0, -0.5],
            "icon-allow-overlap": false,
            "text-allow-overlap": false,
            "symbol-sort-key": styleValueToMapboxStyleValue(iventisLine.lineStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        filter,
        ...addMaxMinZoomLevels(maxZoom, minZoom),
        source: generateLineDistanceMarkerSubLayerSource(iventisLine),
        metadata: {
            name: iventisLine.name,
            type: "line-distance-marker",
            distanceMarkerUnit,
        },
    };

    return distanceMarkerLayer;
}

export function iventisLineToBaseSublayer(iventisLine: MapModuleLayer, id = uuid(), filter?: LineLayerSpecification["filter"]): LineLayerSpecification {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisLine.lineStyle.width.staticValue);

    const layer: LineLayerSpecification = {
        id,
        type: "line",
        paint: {
            "line-color": styleValueToMapboxStyleValue(iventisLine.lineStyle.colour),
            "line-width": styleValueToMapboxStyleValue(iventisLine.lineStyle.width),
            "line-opacity": styleValueToMapboxStyleValue(iventisLine.lineStyle.opacity) ?? 1,
            "line-blur": styleValueToMapboxStyleValue(iventisLine.lineStyle.blur) ?? 0,
            "line-offset": styleValueToMapboxStyleValue(iventisLine.lineStyle.offset) ?? 0,
            "line-dasharray": styleValueParser.lineType(iventisLine.lineStyle.type ?? getDefaultStyleProperty(iventisLine.lineStyle.styleType, "type")),
        },
        layout: {
            visibility: iventisLine.visible ? "visible" : "none",
            "line-cap": getStaticStyleValue(iventisLine.lineStyle.end) != null ? styleValueParser.end(getStaticStyleValue(iventisLine.lineStyle.end)) : "round",
            "line-join": getStaticStyleValue(iventisLine.lineStyle.end) != null ? styleValueParser.join(getStaticStyleValue(iventisLine.lineStyle.join)) : "round",
            "line-sort-key": styleValueToMapboxStyleValue(iventisLine.lineStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        ...addMaxMinZoomLevels(maxZoom, minZoom),
        source: iventisLine.source,
        metadata: {
            name: iventisLine.name,
            type: "base",
        },
    };

    if (filter != null && filter.length !== 0) {
        layer.filter = filter;
    }

    if (iventisLine.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(layer, iventisLine.id);
    }

    return layer;
}

export function iventisAreaToBaseSublayer(iventisArea: MapModuleLayer, id = uuid(), filter: Layer["filter"]): FillLayerSpecification | FillExtrusionLayerSpecification {
    const layer: FillLayerSpecification = {
        id,
        type: "fill",
        paint: {
            "fill-color": styleValueToMapboxStyleValue(iventisArea.areaStyle.colour),
            "fill-opacity": styleValueToMapboxStyleValue(iventisArea.areaStyle.opacity) ?? 1,
            "fill-outline-color": styleValueToMapboxStyleValue(iventisArea.areaStyle.outline) ? "black" : transparentColour,
        },
        layout: {
            visibility: iventisArea.visible ? "visible" : "none",
            "fill-sort-key": styleValueToMapboxStyleValue(iventisArea.areaStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        source: iventisArea.source,
        metadata: {
            name: iventisArea.name,
            type: "base",
        },
    };

    if (filter != null && filter.length !== 0) {
        layer.filter = filter;
    }

    if (iventisArea.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(layer, iventisArea.id);
    }

    return layer;
}

export function iventis3DAreaToBaseSublayer(iventisArea: MapModuleLayer, id = uuid(), filter: Layer["filter"]): FillExtrusionLayerSpecification {
    const layer: FillExtrusionLayerSpecification = {
        id,
        type: "fill-extrusion",
        paint: {
            "fill-extrusion-color": styleValueToMapboxStyleValue(iventisArea.areaStyle.colour) ?? transparentColour,
            "fill-extrusion-opacity": getStaticStyleValue(iventisArea.areaStyle.opacity) ?? 1,
            "fill-extrusion-height": getStaticStyleValue(iventisArea.areaStyle.height) ?? 3,
        },
        layout: {
            visibility: iventisArea.visible ? "visible" : "none",
        },
        source: iventisArea.source,
        metadata: {
            name: iventisArea.name,
            type: "base",
        },
    };

    if (filter) {
        layer.filter = filter;
    }

    if (iventisArea.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(layer, iventisArea.id);
    }
    return layer;
}

export function iventisPointToBaseSublayer(iventisPoint: MapModuleLayer, id = uuid(), filter: Layer["filter"]): CircleLayerSpecification {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisPoint.pointStyle.radius.staticValue);
    const layer: CircleLayerSpecification = {
        id,
        type: "circle",
        paint: {
            "circle-color": styleValueToMapboxStyleValue(iventisPoint.pointStyle.colour),
            "circle-opacity": styleValueToMapboxStyleValue(iventisPoint.pointStyle.opacity),
            "circle-blur": styleValueToMapboxStyleValue(iventisPoint.pointStyle.blur),
            "circle-radius": styleValueToMapboxStyleValue(iventisPoint.pointStyle.radius),
            "circle-stroke-color": styleValueToMapboxStyleValue(iventisPoint.pointStyle.outlineColour) || "#000000",
            "circle-stroke-opacity": styleValueToMapboxStyleValue(iventisPoint.pointStyle.outlineOpacity),
            "circle-stroke-width": styleValueToMapboxStyleValue(iventisPoint.pointStyle.outline) ? styleValueToMapboxStyleValue(iventisPoint.pointStyle.outlineWidth) || 1 : 0,
            "circle-pitch-alignment": (styleValueToMapboxStyleValue(iventisPoint.pointStyle?.pitchAlignment)?.toString()?.toLowerCase() as "map" | "viewport") ?? "viewport",
        },
        layout: {
            visibility: iventisPoint.visible ? "visible" : "none",
            "circle-sort-key": styleValueToMapboxStyleValue(iventisPoint.pointStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        metadata: {
            name: iventisPoint.name,
            type: "base",
        },
        source: iventisPoint.source,
        ...addMaxMinZoomLevels(maxZoom, minZoom),
    };

    if (filter != null && filter.length !== 0) {
        layer.filter = filter;
    }

    if (iventisPoint.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(layer, iventisPoint.id);
    }

    return layer;
}

/**
 * We only want to add the maxZoom and MinZoom filters to our layers if they are different to
 * our mapbox max/min levels, otherwise you can scroll past the max levels making our layer
 * drawing disappear
 */
export function addMaxMinZoomLevels(maxZoom: number, minZoom: number): { maxZoom?: number; minZoom?: number } {
    const objectToReturn = {};

    if (maxZoom != null && maxZoom < MAPBOX_MAX_ZOOM) {
        // eslint-disable-next-line dot-notation
        objectToReturn["maxZoom"] = maxZoom;
    }

    if (minZoom != null && minZoom > MAPBOX_MIN_ZOOM) {
        // eslint-disable-next-line dot-notation
        objectToReturn["minZoom"] = minZoom;
    }
    return objectToReturn;
}

/**
 * Checks if the iconIamge style value contains either default icons or sdf icons
 * @param iconImage - the iconImage style value
 * @param sdfIconIds - an object of mappings between sdfIcons and disabled sdfIcon ids
 * @returns [containsDefaultIcons, containsSdfIcons]
 */
export function checkSdfDefaultIcons(iconImage: StyleValue<string>, sdfIconIds: { [key: string]: string }): [boolean, boolean] {
    let containsDefaultIcons = false;
    let containsSdfIcons = false;
    Object.values({
        ...iconImage.mappedValues,
        staticValue: iconImage.staticValue,
    }).forEach((styleValue) => {
        if (!containsSdfIcons) {
            containsSdfIcons = sdfIconIds[styleValue.staticValue] != null;
        }
        if (!containsDefaultIcons) {
            containsDefaultIcons = sdfIconIds[styleValue.staticValue] == null;
        }
    });

    return [containsDefaultIcons, containsSdfIcons];
}

/**
 * Repalces the icon asset Ids to add the disabeld sdf icon suffix if the style contains both sdf and non-sdf icons in data driven styling
 * @param iconImage - the iconImage style value
 * @param sdfIconIds - an object of mappings between sdfIcons and disabled sdfIcon ids
 * @returns the new style value with iconImage ids replaced
 */
export function replaceIconIdsForSdf(iconImage: StyleValue<string>, sdfIconIds: { [key: string]: string }): StyleValue<string> {
    const [containsDefaultIcons, containsSdfIcons] = checkSdfDefaultIcons(iconImage, sdfIconIds);

    return {
        ...iconImage,
        staticValue: {
            ...iconImage.staticValue,
            staticValue:
                containsDefaultIcons && containsSdfIcons ? sdfIconIds?.[iconImage.staticValue.staticValue] ?? iconImage.staticValue.staticValue : iconImage.staticValue.staticValue,
        },
        mappedValues: Object.keys(iconImage.mappedValues ?? {}).reduce(
            (cum, key) => ({
                ...cum,
                [key]: {
                    ...cum[key],
                    staticValue:
                        containsDefaultIcons && containsSdfIcons
                            ? sdfIconIds?.[iconImage.mappedValues[key].staticValue] ?? iconImage.mappedValues[key].staticValue
                            : iconImage.mappedValues[key].staticValue,
                },
            }),
            iconImage.mappedValues
        ),
    };
}

/**
 * Checks if the given layer contains both sdf enabled and disabled icons in data-driven styling, checks both the static value and each mapped value
 * @param iventisIcon - the icon layer to be checked
 * @returns [containsSdfIcons: boolean, containsDefaultIcons: boolean, sdfIconIds: string[]]
 * @example
 * if (layer.styleType === StyleType.Icon && layer.iconStyle.iconImage.extractionMethod === StyleValueExtractionMethod.Mapped) {
        // Check all icons to see if they're SDF enabled
        const [containsSdfIcons, containsNormalIcons, sdfIconIds] = await containsSdfDefaultIcons(layer);
 }
 */
export async function containsSdfDefaultIcons(iventisIcon: MapModuleLayer, assetOptions: AssetOptions): Promise<[boolean, boolean, string[]]> {
    return Object.values({
        ...iventisIcon.iconStyle.iconImage.mappedValues,
        staticValue: iventisIcon.iconStyle.iconImage.staticValue,
    }).reduce<Promise<[boolean, boolean, string[]]>>(async (cumP, { staticValue: iconId }) => {
        const cum = await cumP;
        if (!cum[2].includes(iconId) && (await assetOptions.isAssetSdf(iconId))) {
            return [true, cum[1], [...cum[2], iconId]];
        }
        return [cum[0], true, cum[2]];
    }, Promise.resolve([false, false, []]));
}

export function iventisIconToBaseSublayer(
    iventisIcon: MapModuleLayer,
    preview: boolean,
    sdfIconIds: { [key: string]: string },
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    id = uuid(),
    filter: Layer["filter"],
    applyTextFieldExpression?: ApplyListItemTextFunction,
    attributeListItemGetter?: EngineInterpreterOptions["getAttributeListItems"]
): SymbolLayerSpecification {
    const { maxZoom, minZoom } = zoomableValueToMinMaxZoomLevels(iventisIcon.iconStyle.size.staticValue);

    const layer: SymbolLayerSpecification = {
        id,
        type: "symbol",
        paint: {
            ...(iventisIcon.iconStyle.customColour?.staticValue.staticValue && { "icon-color": styleValueToMapboxStyleValue(iventisIcon.iconStyle.colour) }),
            "icon-opacity": styleValueToMapboxStyleValue(iventisIcon.iconStyle.opacity ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "opacity")),
        },
        layout: {
            "icon-image": styleValueToMapboxStyleValue(
                replaceIconIdsForSdf(iventisIcon.iconStyle.iconImage, sdfIconIds) ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "iconImage")
            ),
            "icon-size": styleValueToMapboxStyleValue(iventisIcon.iconStyle.size ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "size")),
            "icon-rotate": iconRotationValueToMapboxStyleValue(iventisIcon.iconStyle.rotation, lengthAndArea.projectDataFields),
            "icon-allow-overlap": getStaticStyleValue(iventisIcon.iconStyle.allowOverlap ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "allowOverlap")),
            "icon-pitch-alignment": styleValueParser.iconOrientation(
                getStaticStyleValue(iventisIcon.iconStyle.orientation ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "orientation"))
            ),
            "icon-rotation-alignment": styleValueParser.iconAlignment(
                getStaticStyleValue(iventisIcon.iconStyle.iconAlignment ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "iconAlignment"))
            ),
            visibility: iventisIcon.visible ? "visible" : "none",
            "symbol-sort-key": styleValueToMapboxStyleValue(iventisIcon.iconStyle.objectOrder ?? createStaticStyleValue(0)),
        },
        ...addMaxMinZoomLevels(maxZoom, minZoom),
        source: iventisIcon.source,
        metadata: {
            type: "base",
            name: iventisIcon.name,
        },
    };

    if (getStaticStyleValue(iventisIcon.iconStyle.text)) {
        layer.paint = {
            ...layer.paint,
            "text-color": getStaticStyleValue(iventisIcon.iconStyle.textColour ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textColour")),
            "text-opacity": getStaticStyleValue(iventisIcon.iconStyle.textOpacity ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textOpacity")),
            "text-halo-color": getStaticStyleValue(iventisIcon.iconStyle.textOutlineColour ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textOutlineColour")),
            "text-halo-width": getStaticStyleValue(iventisIcon.iconStyle.textOutlineWidth ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textOutlineWidth")),
        };

        let textContentValue = iventisIcon.iconStyle.textContent;

        if (textContentValue == null) {
            textContentValue = getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textContent");
        }

        layer.layout = {
            ...layer.layout,
            "text-field": textContentValueToMapboxStyleValue(textContentValue, lengthAndArea, id, iventisIcon.id, applyTextFieldExpression, attributeListItemGetter),
            "text-size": styleValueToMapboxStyleValue(iventisIcon.iconStyle.textSize ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textSize")),
            "text-allow-overlap": getStaticStyleValue(iventisIcon.iconStyle.textOverlap ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textOverlap")),
            "text-font": getStaticStyleValue(iventisIcon.iconStyle.textBold ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textBold"))
                ? boldFontStack
                : regularFontStack,
            "text-variable-anchor": styleValueParser.textPosition(
                getStaticStyleValue(iventisIcon.iconStyle.textPosition ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textPosition")),
                preview
            ),
            "text-radial-offset": styleValueParser.textOffset(
                getStaticStyleValue(iventisIcon.iconStyle.textOffset ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textOffset")),
                iventisIcon.iconStyle.textSize ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "textSize")
            ),
            "icon-text-fit": styleValueParser.iconTextFit(
                getStaticStyleValue(iventisIcon.iconStyle.iconTextFit ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "iconTextFit"))
            ),
            "icon-text-fit-padding": styleValueParser.iconTextFitMargin(
                getStaticStyleValue(iventisIcon.iconStyle.iconTextFitMargin ?? getDefaultStyleProperty(iventisIcon.iconStyle.styleType, "iconTextFitMargin"))
            ),
        };
    }

    if (iventisIcon.storageScope === LayerStorageScope.LocalAndTiles) {
        setRemoteSourceLayer(layer, iventisIcon.id);
    }

    if (filter != null) {
        layer.filter = filter;
    }

    return layer;
}

/**
 * Sets the 'source-layer' on the provided mapbox layer if the map module layer is remote
 * @param mapboxLayer The mapbox layer object to modify the source-layer of
 * @param mapModuleLayerId The layer ID to test for remote-ness and used to produce the source-layer
 * @param overrideSourceLayer If specified, is applied to the 'source-layer' property supposing the layer is remote
 */
export function setRemoteSourceLayer(mapboxLayer: Layer, mapModuleLayerId: string, overrideSourceLayer?: string) {
    if (!mapModuleLayerId.includes(`_${localSuffix}`)) {
        // eslint-disable-next-line no-param-reassign
        mapboxLayer["source-layer"] = overrideSourceLayer || removeLayerIdSuffixes(mapModuleLayerId);
    }
}

export const styleValueParser = {
    join: (value: LineJoin): LineJoinString => value.toLowerCase() as LineJoinString,
    end: (value: LineEnd): LineEndString => value.toLowerCase() as LineEndString,
    iconPlacement: (value: IconPlacement): SymbolLayerSpecification["layout"]["symbol-placement"] => {
        if (value === IconPlacement.LineCenter) {
            return "line-center";
        }
        if (value === IconPlacement.Line) {
            return "line";
        }
        if (value === IconPlacement.Point) {
            return "point";
        }
        throw new Error(`Icon placement value ${value} not recognised`);
    },
    iconOrientation: (value: IconOrientation): SymbolLayerSpecification["layout"]["icon-rotation-alignment"] => {
        switch (value) {
            case IconOrientation.Flatten:
                return "map";
            case IconOrientation.Raise:
                return "viewport";
            default:
                throw new Error(`Icon orientation value of ${value} could not be parsed`);
        }
    },
    lineType: (value: LineStyle["type"]): LineLayerSpecification["paint"]["line-dasharray"] => {
        const mapboxValue = styleValueToMapboxStyleValue(value);

        const parseValue = (value: LineType) => {
            switch (value) {
                case LineType.Solid:
                    return [1, 0];
                case LineType.Dashed:
                    return [2, 2];
                case LineType.Dotted:
                    return [0.1, 2];
                case LineType.SmallDash:
                    return [1, 2];
                case LineType.LargeDash:
                    return [3, 3];
                default:
                    throw new Error(`Line type value of ${value} could not be parsed`);
            }
        };

        if (Array.isArray(mapboxValue)) {
            return mapboxValue.map((val) => {
                if (typeof val === "string" && val !== "case") {
                    return parseValue(val as LineType);
                }
                return val;
            });
        }
        return parseValue(mapboxValue as LineType);
    },
    /**
     * Anchor position is the opposite of where the text is positioned for example if the anchor value is top
     * the position of the text will be below the object
     */
    textPosition: (value: TextPosition, preview: boolean): SymbolLayerSpecification["layout"]["text-variable-anchor"] => {
        switch (value) {
            case TextPosition.Automatic:
                return preview ? ["center"] : automaticTextAnchor;
            case TextPosition.Centre:
                return ["center"];
            case TextPosition.Above:
                return ["bottom"];
            case TextPosition.Below:
                return ["top"];
            case TextPosition.Right:
                return ["left"];
            case TextPosition.Left:
                return ["right"];
            default:
                throw new Error(`Text position value of ${value} could not be parsed`);
        }
    },
    /**
     * Text offset for mapbox is measured in ems: text size in pixels = 1em
     * Convert offset form pixels to ems pixels: offset (px) / font size (px)
     * For zoomable values the font size is the largest font size
     */
    textOffset: (textOffsetPixels: number, textSizePixels: StyleValue<number>): SymbolLayerSpecification["layout"]["text-radial-offset"] => {
        let fontSize: number;
        if (textSizePixels.staticValue.extractionMethod === ZoomableValueExtractionMethod.Static) {
            fontSize = textSizePixels.staticValue.staticValue;
        } else {
            // Get the largest font size from the mapped values
            fontSize = Math.max(...Object.values(textSizePixels.staticValue.mappedZoomValues).map((zoomLevel) => zoomLevel.value));
        }
        if (fontSize <= 0) {
            return 0;
        }
        return textOffsetPixels / fontSize;
    },
    iconTextFit: (value: boolean): SymbolLayerSpecification["layout"]["icon-text-fit"] => (value ? "both" : "none"),
    iconTextFitMargin: (value: number): SymbolLayerSpecification["layout"]["icon-text-fit-padding"] => [value, value, value, value],
    /* When symbol spacing is 0 it will crash the map so ensure it is never */
    symbolSpacing: (value: number): SymbolLayerSpecification["layout"]["symbol-spacing"] =>
        value === 0 ? getDefaultStyleProperty(StyleType.Line, "arrowSpacing").staticValue.staticValue : value,
    iconAlignment: (value: IconAlignment): SymbolLayerSpecification["layout"]["icon-rotation-alignment"] => {
        switch (value) {
            case IconAlignment.North:
                return "map";
            case IconAlignment.Screen:
                return "viewport";
            default:
                throw new Error(`Icon alignment value of ${value} could not be parsed`);
        }
    },
};

/** Offset to apply to outline sub layer style to ensure the layer is not overlapping the polygon */
export function calculateOffsetForPolygonOutline(outlineWidth: number) {
    return (outlineWidth / 2) * -1;
}

/**
 * Calculates the rendered line-outline width.
 * For zoomable static styles, this combines base and outline zoom curves into one top-level mapbox expression.
 * @param baseLineWidth The base line width style value.
 * @param outlineWidth The outline width style value.
 * @returns The combined line width as a number or mapbox expression.
 */
export function calculateLineWidthForLineOutline(baseLineWidth: StyleValue<number>, outlineWidth?: StyleValue<number>): number | ExpressionSpecification {
    // Mapbox requires zoom-based expressions to be top-level. Combine zoom curves directly for static style values.
    if (baseLineWidth.extractionMethod === StyleValueExtractionMethod.Static && (outlineWidth == null || outlineWidth.extractionMethod === StyleValueExtractionMethod.Static)) {
        return calculateZoomableLineWidthForLineOutline(baseLineWidth.staticValue, outlineWidth?.staticValue ?? createStaticStyleValue(0).staticValue);
    }

    const lineWidth = styleValueToMapboxStyleValue(baseLineWidth);
    const width = styleValueToMapboxStyleValue(outlineWidth ?? createStaticStyleValue(0));

    if (typeof lineWidth === "number" && typeof width === "number") {
        return lineWidth + width;
    }

    return width;
}

/**
 * Combines base and outline zoomable widths into a single output width.
 * When the base width is hidden at a zoom level, the combined output is forced to 0.
 * @param baseLineWidth The base line width by zoom.
 * @param outlineWidth The outline width by zoom.
 * @returns A static width or an interpolate expression over zoom.
 */
function calculateZoomableLineWidthForLineOutline(baseLineWidth: ZoomableValue<number>, outlineWidth: ZoomableValue<number>): number | ExpressionSpecification {
    // Both widths are static, so we can just add them together and return a static value
    if (!hasMappedZoomValues(baseLineWidth) && !hasMappedZoomValues(outlineWidth)) {
        return baseLineWidth.staticValue + outlineWidth.staticValue;
    }

    const zoomLevels = getSortedZoomLevels(baseLineWidth, outlineWidth);
    // If there are no zoom levels, just return the sum of the static values
    if (zoomLevels.length === 0) {
        return baseLineWidth.staticValue + outlineWidth.staticValue;
    }

    const baseMappedZoomValues = getSortedMappedZoomValues(baseLineWidth);
    const zoomValuePairs = zoomLevels.flatMap((zoomLevel) => {
        // If base width is hidden at this zoom, preserve hidden state for the combined curve.
        if (isHiddenAtZoom(baseMappedZoomValues, zoomLevel)) {
            return [zoomLevel, 0];
        }
        return [zoomLevel, getValueAtZoom(baseLineWidth, zoomLevel) + getValueAtZoom(outlineWidth, zoomLevel)];
    });

    // Return a mapbox expression that interpolates the combined width at each zoom level
    return ["interpolate", ["linear"], ["zoom"], ...zoomValuePairs] as ExpressionSpecification;
}

/**
 * Checks whether a zoomable value has continuous values set.
 * @param zoomableValue The zoomable value to inspect.
 * @returns True when mapped zoom values are present.
 */
function hasMappedZoomValues(zoomableValue: ZoomableValue<number>) {
    return (
        zoomableValue.extractionMethod === ZoomableValueExtractionMethod.Continuous &&
        zoomableValue.mappedZoomValues != null &&
        Object.keys(zoomableValue.mappedZoomValues).length > 0
    );
}

/**
 * Builds a sorted list of unique zoom levels from one or more zoomable values.
 * @param zoomableValues The zoomable values to read levels from.
 * @returns Ascending list of zoom levels.
 */
function getSortedZoomLevels(...zoomableValues: ZoomableValue<number>[]) {
    const zoomLevelSet = new Set<number>();

    zoomableValues.forEach((zoomableValue) => {
        if (zoomableValue.extractionMethod !== ZoomableValueExtractionMethod.Continuous || zoomableValue.mappedZoomValues == null) {
            return;
        }

        Object.keys(zoomableValue.mappedZoomValues).forEach((zoomLevel) => {
            zoomLevelSet.add(Number.parseFloat(zoomLevel));
        });
    });

    return [...zoomLevelSet].sort((a, b) => a - b);
}

/**
 * Gets the effective numeric value at a specific zoom level.
 * @param zoomableValue The zoomable value to evaluate.
 * @param zoomLevel The zoom level to evaluate at.
 * @returns The static or interpolated value for the requested zoom level.
 */
function getValueAtZoom(zoomableValue: ZoomableValue<number>, zoomLevel: number) {
    const mappedZoomValues = getSortedMappedZoomValues(zoomableValue);
    if (mappedZoomValues.length === 0) {
        return zoomableValue.staticValue;
    }

    if (zoomableValue.extractionMethod === ZoomableValueExtractionMethod.Continuous) {
        return getInterpolatedValueAtZoom(mappedZoomValues, zoomLevel);
    }

    return zoomableValue.staticValue;
}

/**
 * Converts mapped zoom values into a sorted array for easier interpolation/lookups.
 * Hidden values are normalized to numeric value 0 while retaining a hidden flag.
 * @param zoomableValue The zoomable value to normalize.
 * @returns Sorted mapped zoom values.
 */
function getSortedMappedZoomValues(zoomableValue: ZoomableValue<number>) {
    if (zoomableValue.mappedZoomValues == null) {
        return [] as Array<{ zoomLevel: number; value: number; hidden: boolean }>;
    }

    return Object.entries(zoomableValue.mappedZoomValues)
        .map(([zoomLevel, zoomValue]) => ({ zoomLevel: Number.parseFloat(zoomLevel), value: zoomValue.hidden ? 0 : zoomValue.value, hidden: Boolean(zoomValue.hidden) }))
        .sort((a, b) => a.zoomLevel - b.zoomLevel);
}

/**
 * Determines whether the line should be hidden at a specific zoom level.
 * The hidden state follows the nearest lower mapped zoom breakpoint.
 * @param mappedZoomValues Sorted mapped zoom values.
 * @param zoomLevel The zoom level to evaluate.
 * @returns True if hidden at the requested zoom level.
 */
function isHiddenAtZoom(mappedZoomValues: Array<{ zoomLevel: number; value: number; hidden: boolean }>, zoomLevel: number) {
    if (mappedZoomValues.length === 0) {
        return false;
    }

    if (zoomLevel <= mappedZoomValues[0].zoomLevel) {
        return mappedZoomValues[0].hidden;
    }

    for (let i = 0; i < mappedZoomValues.length - 1; ++i) {
        const start = mappedZoomValues[i];
        const end = mappedZoomValues[i + 1];

        if (zoomLevel === start.zoomLevel) {
            return start.hidden;
        }

        if (zoomLevel < end.zoomLevel) {
            return start.hidden;
        }
    }

    return mappedZoomValues[mappedZoomValues.length - 1].hidden;
}

/**
 * Linearly interpolates a numeric value for a given zoom level.
 * @param mappedZoomValues Sorted mapped zoom values.
 * @param zoomLevel The zoom level to evaluate.
 * @returns Interpolated value at the requested zoom level.
 */
function getInterpolatedValueAtZoom(mappedZoomValues: Array<{ zoomLevel: number; value: number; hidden: boolean }>, zoomLevel: number) {
    if (mappedZoomValues.length === 0) {
        return 0;
    }

    if (zoomLevel <= mappedZoomValues[0].zoomLevel) {
        return mappedZoomValues[0].value;
    }

    for (let i = 0; i < mappedZoomValues.length - 1; ++i) {
        const start = mappedZoomValues[i];
        const end = mappedZoomValues[i + 1];

        if (zoomLevel === start.zoomLevel) {
            return start.value;
        }

        if (zoomLevel < end.zoomLevel) {
            // Calculate the fraction of the way between the two zoom levels and interpolate the value accordingly
            const fractionOfZoomLevels = (zoomLevel - start.zoomLevel) / (end.zoomLevel - start.zoomLevel);
            return start.value + (end.value - start.value) * fractionOfZoomLevels;
        }
    }

    return mappedZoomValues[mappedZoomValues.length - 1].value;
}

/**
 * Converts a domain StyleValue to a mapbox style literal value, or data driven value (expressed as an expression)
 */
export function styleValueToMapboxStyleValue<T>(styleValue: StyleValue<T>) {
    if (styleValue.extractionMethod === StyleValueExtractionMethod.Static) {
        return getZoomableValueExpression(styleValue.staticValue);
    }

    // If the style is data driven however only a default value is set and there is not mapped values
    if (styleValue.extractionMethod === StyleValueExtractionMethod.Mapped && Object.keys(styleValue?.mappedValues).length === 0) {
        return getStaticStyleValueFromMapped(styleValue);
    }

    if (styleValue.extractionMethod === StyleValueExtractionMethod.Mapped) {
        // Expression to get the value of the data field on a mapboxgl feature (object)
        const getDataFieldValue: ExpressionSpecification = ["get", styleValue.dataFieldId];

        /*
            Uses mapbox expressions.
            Operation is on the left, then the right hand side is one or more parameters applied to that operator.
            Expressions are nested.
            Effectively produces a number of conditionals with the flatMap roughly like so:

            if (getDataFieldValue === listItemId) {
                return <<The colour for that list item>>
            }

            But in the form of mapbox expressions
        */

        const caseExpression = Object.entries(styleValue.mappedValues).flatMap(([listItemId, zoomableValue]: [string, ZoomableValue<T>]) => [
            ["==", listItemId, getDataFieldValue] as ExpressionSpecification,
            zoomableValue.staticValue,
        ]);

        const expression: ExpressionSpecification = ["case", ...caseExpression, styleValue.staticValue.staticValue];

        return expression;
    }

    // Should apply the literal value of the data attribute
    if (styleValue.extractionMethod === StyleValueExtractionMethod.Literal) {
        const expression: ExpressionSpecification = ["get", styleValue.dataFieldId];
        return expression;
    }

    throw new Error(`No case handled for extraction method ${styleValue.extractionMethod}`);
}

/**
 * Converts the text content style value to a mapbox style value.
 * Expects a multi extraction method styled value with literal/mapped extracted styled multi values.
 * Also supports older styles which are directly literal/mapped extracted styles.
 * @param styleValue The mapbox layer object to modify the source-layer of
 * @param lengthAndArea The length and area data fields and unit of measurement
 * @param mapboxTextLayerId The mapbox layer id to apply the text field expression to
 * @param iventisLayerId The iventis layer id to apply the text field expression to
 * @param applyTextFieldExpression The function to apply the text field expression to the mapbox layer when list items are required to be fetched
 * @param getAttributeListItems The function to get the list items for the data field
 */
export function textContentValueToMapboxStyleValue<T>(
    styleValue: StyleValue<T>,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    mapboxTextLayerId: string,
    iventisLayerId: string,
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttributeListItems?: EngineInterpreterOptions["getAttributeListItems"]
) {
    // Flatten style value to an array of text content style values.
    let flatStyleValues = flattenStyleValue(styleValue);
    // if getAttributeListItems or applyTextFieldExpression is null, we cannot generate text content expressions for mapped extraction styles.
    if (getAttributeListItems == null || applyTextFieldExpression == null) {
        flatStyleValues = flatStyleValues.filter((sv) => sv.extractionMethod !== StyleValueExtractionMethod.Mapped);
    }

    const getExpression = (styleValue: StyleValue<T>, listItems: DataFieldListItem[]) => {
        if (styleValue.extractionMethod === StyleValueExtractionMethod.Static) {
            // We don't have static text content, so return nothing
            return "" as T;
        }
        if (styleValue.extractionMethod === StyleValueExtractionMethod.Mapped) {
            // Expression to get the value of the data field on a mapboxgl feature (object)
            const getDataFieldValue: ExpressionSpecification = ["get", styleValue.dataFieldId];

            const caseExpression = listItems.flatMap(({ id, name }) =>
                // Set the name of the list item as our stop value
                [["==", id, getDataFieldValue] as ExpressionSpecification, name]
            );

            return ["case", ...caseExpression, ""];
        }

        // Should apply the literal value of the data attribute
        if (styleValue.extractionMethod === StyleValueExtractionMethod.Literal) {
            const { projectDataFields, unitOfMeasurement } = lengthAndArea;
            const areaDataField = getMapObjectAreaSystemDataField(projectDataFields);
            const lengthDataField = getMapObjectLengthSystemDataField(projectDataFields);

            if (areaDataField?.id === styleValue.dataFieldId) {
                return createAreaExpression(unitOfMeasurement, styleValue.dataFieldId);
            }

            if (lengthDataField?.id === styleValue.dataFieldId) {
                return createLengthExpression(unitOfMeasurement, styleValue.dataFieldId);
            }

            const expression: ExpressionSpecification = ["get", styleValue.dataFieldId];
            return expression;
        }

        throw new Error(`No case handled for extraction method ${styleValue.extractionMethod} on textContent`);
    };

    const convertFlatStylesToExpressions = (flatStyleValues: StyleValue<T>[], listItems: DataFieldListItem[]) => {
        const textContextExpression: T | ExpressionSpecification = ["concat"];
        if (flatStyleValues.length === 0) {
            return "" as T;
        }

        flatStyleValues.forEach((value, index) => {
            if (index > 0) {
                textContextExpression.push("\n\n");
            }
            textContextExpression.push(
                getExpression(
                    value,
                    listItems.filter((listItem) => listItem.dataFieldId === value.dataFieldId)
                )
            );
        });
        return textContextExpression;
    };

    // If there are any mapped style values, we need to get the list items for them
    if (flatStyleValues.some((sv) => sv.extractionMethod === StyleValueExtractionMethod.Mapped)) {
        const listItemPromises: Promise<DataFieldListItem[]>[] = flatStyleValues
            .filter((sv) => sv.extractionMethod === "Mapped")
            .map((sv) => getAttributeListItems(sv.dataFieldId));
        Promise.all(listItemPromises).then((listItems) => {
            const expression = convertFlatStylesToExpressions(
                flatStyleValues,
                listItems.flatMap((listItem) => listItem)
            );
            applyTextFieldExpression(expression as ExpressionSpecification, mapboxTextLayerId, iventisLayerId);
        });
        return "" as T;
    }
    return convertFlatStylesToExpressions(flatStyleValues, []);
}

function flattenStyleValue<T>(styleValue: StyleValue<T>): StyleValue<T>[] {
    if (styleValue.extractionMethod !== StyleValueExtractionMethod.Multi) {
        return [styleValue];
    }
    return styleValue?.multiValues || [];
}

/** By default we use Continuous for Mapbox */
export function getZoomableValueExpression<T>(zoomableValue: ZoomableValue<T>): ExpressionSpecification | T {
    // NOTE: step & interpolate expressions with a zoom input value cannot be used unless the expression exists in the top-level
    // In other words, currently zoom expressions only work with static style values

    const generateExpression = (zoomLevel: number, value: ZoomValue<T>) => (value.hidden ? [zoomLevel, 0] : [zoomLevel, value.value]);

    switch (zoomableValue.extractionMethod) {
        case ZoomableValueExtractionMethod.Static: {
            return zoomableValue.staticValue;
        }
        case ZoomableValueExtractionMethod.Continuous: {
            // Bug 17224 - Map loads as white when a "Line width" has an incorrect value
            if (zoomableValue.mappedZoomValues == null || Object.keys(zoomableValue.mappedZoomValues).length === 0) {
                return zoomableValue.staticValue;
            }

            const expressions = Object.entries(zoomableValue.mappedZoomValues).flatMap<unknown[], unknown>(([zoomLevel, fundementalStyleValue]) =>
                generateExpression(Number.parseFloat(zoomLevel), fundementalStyleValue)
            );
            return ["interpolate", ["linear"], ["zoom"], ...expressions] as ExpressionSpecification;
        }
        case ZoomableValueExtractionMethod.Closest: {
            // Bug 17224 - Map loads as white when a "Line width" has an incorrect value
            if (zoomableValue.mappedZoomValues == null || Object.keys(zoomableValue.mappedZoomValues).length === 0) {
                return zoomableValue.staticValue;
            }

            const expressions = Object.entries(zoomableValue.mappedZoomValues).flatMap<unknown[], unknown>(([zoomLevel, fundementalStyleValue]) =>
                generateExpression(Number.parseFloat(zoomLevel), fundementalStyleValue)
            );
            return ["step", ["zoom"], zoomableValue.staticValue, ...expressions] as ExpressionSpecification;
        }
        default: {
            return zoomableValue.staticValue;
        }
    }
}

export function zoomableValueToMinMaxZoomLevels<T>(zoomableValue: ZoomableValue<T>): { minZoom: number; maxZoom: number } {
    if (zoomableValue.extractionMethod !== ZoomableValueExtractionMethod.Static) {
        // Default our values to pos and neg infinity
        const defaultZoomLevels = { maxZoom: Number.POSITIVE_INFINITY, minZoom: Number.NEGATIVE_INFINITY };

        // Sort our array by zoom levels
        const sorted = Object.entries(zoomableValue.mappedZoomValues).map(([zoomLevel, zoomValue]) => ({ zoomLevel: Number.parseFloat(zoomLevel), hidden: zoomValue.hidden }));
        sorted.sort(({ zoomLevel: zoomLevelA }, { zoomLevel: zoomLevelB }) => zoomLevelA - zoomLevelB);

        // Sorts if we are trying to find our maximum zoom level
        let min = true;
        sorted.forEach(({ zoomLevel, hidden }) => {
            // If we are trying to find our maximum zoom level and have come across a hidden zoom level
            if (!min && hidden) {
                // make our maximum the smallest zoomlevel between our max and our zoom level
                // we do this because if we consider:
                // 16 - hidden
                // 11 - hidden
                // 6  - not hidden
                // 3  - hidden
                // we want our max to be 11
                defaultZoomLevels.maxZoom = Math.min(zoomLevel, defaultZoomLevels.maxZoom);
            } else if (min && hidden) {
                // make our minimum the largest zoomlevel between our min and our zoom level
                // we do this because if we consider:
                // 16 - hidden
                // 11 - hidden
                // 6  - not hidden
                // 3  - hidden
                // we want our min to be 3
                defaultZoomLevels.minZoom = Math.max(zoomLevel, defaultZoomLevels.minZoom);
            }
            // Since this array is ordered, if we come across one that is not hidden we can swap what we are looking for
            if (!hidden) {
                min = false;
            }
        });
        return {
            maxZoom: defaultZoomLevels.maxZoom === Number.POSITIVE_INFINITY ? 24 : defaultZoomLevels.maxZoom,
            minZoom: defaultZoomLevels.minZoom === Number.NEGATIVE_INFINITY ? 0 : defaultZoomLevels.minZoom,
        };
    }
    // Default our max and min to the limits set by mapbox
    return { maxZoom: 24, minZoom: 0 };
}

export function setMapboxTextPropertiesOnIcon(
    baseLayerId: string,
    layer: BasicMapLayer,
    styleChanges: StylePropertyToValueMap<IconStyle>[],
    preview: boolean,
    map: Map,
    lengthAndArea: {
        projectDataFields: DataField[];
        unitOfMeasurement: UnitOfMeasurement;
    },
    applyTextFieldExpression?: ApplyListItemTextFunction,
    getAttributeListItems?: EngineInterpreterOptions["getAttributeListItems"]
) {
    const textStyle = getTextStyle(layer);

    Object.keys(textStyle).forEach((textStyleProperty) => {
        // Get the most recent value
        const value = styleChanges.find((change) => change.styleProperty === textStyleProperty)?.value ?? textStyle[textStyleProperty];
        const mapboxProperty = iventisToMapboxTextStyleProperty[textStyleProperty];
        switch (textStyleProperty) {
            case "textColour":
            case "textOutlineColour":
            case "textOutlineWidth":
            case "textOpacity":
                map.setPaintProperty(baseLayerId, mapboxProperty, styleValueToMapboxStyleValue(value));
                break;
            case "textOverlap":
                map.setLayoutProperty(baseLayerId, mapboxProperty, styleValueToMapboxStyleValue(value));
                break;
            case "text": {
                const textContentValue = (styleChanges.find((change) => change.styleProperty === "textContent")?.value as StyleValue<string>) ?? textStyle.textContent;
                map.setLayoutProperty(
                    baseLayerId,
                    mapboxProperty,
                    getStaticStyleValue(value)
                        ? textContentValueToMapboxStyleValue(textContentValue, lengthAndArea, baseLayerId, layer.id, applyTextFieldExpression, getAttributeListItems)
                        : ""
                );
                break;
            }
            case "textSize": {
                map.setLayoutProperty(baseLayerId, mapboxProperty, styleValueToMapboxStyleValue(value));
                // Text size is correlated to text offset therefore offset to be changed when size changes
                const offsetValue = (styleChanges.find((change) => change.styleProperty === "textOffset")?.value as StyleValue<number>) ?? textStyle.textOffset;
                map.setLayoutProperty(baseLayerId, "text-radial-offset", styleValueParser.textOffset(getStaticStyleValue<number>(offsetValue), value));
                break;
            }
            case "textBold":
                map.setLayoutProperty(baseLayerId, mapboxProperty, styleValueToMapboxStyleValue(value) ? boldFontStack : regularFontStack);
                break;
            case "textContent":
                {
                    const textValue = (styleChanges.find((change) => change.styleProperty === "text")?.value as StyleValue<boolean>) ?? textStyle.text;
                    map.setLayoutProperty(
                        baseLayerId,
                        mapboxProperty,
                        getStaticStyleValue(textValue)
                            ? textContentValueToMapboxStyleValue(value, lengthAndArea, baseLayerId, layer.id, applyTextFieldExpression, getAttributeListItems)
                            : ""
                    );
                }
                break;
            case "textPosition":
                map.setLayoutProperty(baseLayerId, mapboxProperty, styleValueParser.textPosition(getStaticStyleValue(value), preview));
                break;
            case "textOffset": {
                const textSizeValue = (styleChanges.find((change) => change.styleProperty === "textSize")?.value as StyleValue<number>) ?? textStyle.textSize;
                map.setLayoutProperty(baseLayerId, mapboxProperty, styleValueParser.textOffset(getStaticStyleValue(value), textSizeValue));
                break;
            }
            // Change of style type is handled in the calling function
            // Other style properties are not implemented
            case "styleType":
            case "textItalic":
            case "textUnderlined":
            case "objectOrder":
                break;
            default:
                throw new Error(`Text style property ${textStyleProperty} was not handled`);
        }
    });
}

export function getInitialMapPosition(state: MapState, useMapObjectBounds: boolean, exportOptions?: ExportOptions): Partial<MapboxInitialPosition> {
    if (exportOptions?.bounds) {
        return { bounds: [...exportOptions.bounds[0], ...exportOptions.bounds[1]] as [number, number, number, number] };
    }

    if (useMapObjectBounds && state.tileSources.value?.objects.bounds != null) {
        return { bounds: bbox(state.tileSources.value.objects.bounds) as BBox2d, fitBoundsOptions: { padding: 20, maxZoom: 16 } };
    }

    return {
        center: [state.position.value.lng, state.position.value.lat],
        zoom: state.position.value.zoom ?? MAPBOX_FALLBACK_ZOOM,
        bearing: state.position.value.bearing,
        pitch: state.position.value.pitch,
    };
}

/** Gets all the icon ids in an array of layers */
export function getAllLayerIconsIds(layers: MapModuleLayer[]): string[] {
    const iconIds: string[] = [];
    layers.forEach((layer) => {
        if (layer.styleType === StyleType.Icon) {
            const ids = getStaticAndMappedValues(layer.iconStyle.iconImage);
            iconIds.push(...ids);
        }
    });
    // Remove all duplicates
    return [...new Set(iconIds)];
}

/** Returns a single aggregate layer where inputted layer id is an id of one of the sub layers */
export function getAggregateLayerBySubLayerId(aggregateLayers: AggregateLayers, layerId: string) {
    if (aggregateLayers === undefined) {
        return [];
    }
    return Object.values(aggregateLayers).find((layers) => layers.find((layer) => layer.id === layerId));
}

/** Sorts an array of sub layers, ensuring SubLayerType.BASE is index 0
 *
 * Important for ordering which requires SubLayerType.BASE to be added first
 */
export function sortSubLayers(subLayers?: MapboxlayerWithSublayerType[]) {
    if (subLayers == null) {
        return [];
    }
    return subLayers.sort((a, b) => getSubLayerOrderValue(a.type) - getSubLayerOrderValue(b.type));
}

function getSubLayerOrderValue(subLayerType: SubLayerType) {
    if (subLayerType === SubLayerType.BASE) {
        return 0;
    }
    return 1;
}

export function getAggregateLayerIdFromSubLayerId(layerId: string, aggregateLayers: AggregateLayers): string {
    for (const aggregateLayerId in aggregateLayers) {
        if (aggregateLayers[aggregateLayerId].some((layer) => layer.id === layerId)) {
            return aggregateLayerId;
        }
    }
    return undefined;
}

/** Checks if a sub layer of an aggregate layer is a local layer */
export function isAggregateSubLayerLocal(layerId: string, aggregateLayers: AggregateLayers) {
    return getAggregateLayerIdFromSubLayerId(layerId, aggregateLayers)?.includes(localSuffix);
}

/** Returns number of remote layers in given input */
export function amountOfRemoteLayers(layers: MapModuleLayer[]): number {
    if (layers == null) {
        return 0;
    }

    const remoteOnlyLayers = layers.filter((layer) => layer.remote);
    return remoteOnlyLayers.length;
}

export function getSourceBounds(mapObjectBounds: Polygon | MultiPolygon | string, emptySource: boolean): [number, number, number, number] {
    if (emptySource) {
        return [0, 0, 0, 0];
    }
    if (mapObjectBounds != null && typeof mapObjectBounds !== "string") {
        return bbox(mapObjectBounds) as [number, number, number, number];
    }
    return null;
}

/**
 *  Creates a mapbox expression to show the MapObjectArea DataField
 *
 *  Will convert the value shown to imperial or metric
 */
function createAreaExpression(unitOfMeasurement: UnitOfMeasurement, dataFieldId: string) {
    const expression: ExpressionSpecification =
        unitOfMeasurement === UnitOfMeasurement.Imperial
            ? [
                  "case",
                  ["<", ["*", ["get", dataFieldId], squareFeetToSquareMeter], squaredFeetInSquaredMile * smallestMilesValueToShow],
                  ["concat", mapboxExpression2dp(["*", ["get", dataFieldId], squareFeetToSquareMeter]), "ft\u00B2"],
                  ["concat", mapboxExpression2dp(["/", ["get", dataFieldId], squareMeterToSquareMile]), "mi\u00B2"],
              ]
            : [
                  "case",
                  ["<", ["get", dataFieldId], metersSquaredInKiloMeterSquared * smallestKilometerValueToShow],
                  ["concat", ["get", dataFieldId], "m\u00B2"],
                  ["concat", mapboxExpression2dp(["/", ["get", dataFieldId], metersSquaredInKiloMeterSquared]), "km\u00B2"],
              ];
    return expression;
}

/**
 * Creates a mapbox expression to show the MapObjectLength DataField
 *
 *  Will convert the value shown to imperial or metric
 */
function createLengthExpression(unitOfMeasurement: UnitOfMeasurement, dataFieldId: string) {
    const expression: ExpressionSpecification =
        unitOfMeasurement === UnitOfMeasurement.Imperial
            ? [
                  "case",
                  ["<", ["*", ["get", dataFieldId], feetToMeter], feetInMile],
                  ["concat", mapboxExpression2dp(["*", ["get", dataFieldId], feetToMeter]), "ft"],
                  ["concat", mapboxExpression2dp(["/", ["get", dataFieldId], mileToMeter]), "mi"],
              ]
            : [
                  "case",
                  ["<", ["get", dataFieldId], metersInKilometer],
                  ["concat", ["get", dataFieldId], "m"],
                  ["concat", mapboxExpression2dp(["/", ["get", dataFieldId], metersInKilometer]), "km"],
              ];
    return expression;
}

/** Rounds a number to two decimal places using mapbox expression */
function mapboxExpression2dp(expressionToRound: ExpressionSpecification): ExpressionSpecification {
    return ["/", ["round", ["*", expressionToRound, 100]], 100];
}

export function getLineModelGuideLineLayerId(layerId: string) {
    return `${layerId}-guide`;
}

export function createBaseMapboxStyle(fontStackUrl: string): StyleSpecification {
    return {
        version: 8,
        name: "iventis",
        glyphs: fontStackUrl,
        sources: {},
        layers: [
            {
                id: IVENTIS_MAP_BACKGROUND_SLOT_ID,
                type: "slot",
            },
            {
                id: IVENTIS_SITEMAP_RASTER_SLOT_ID,
                type: "slot",
            },
            {
                id: IVENTIS_SITEMAP_AREA_SLOT_ID,
                type: "slot",
            },
            {
                id: IVENTIS_SITEMAP_LINE_SLOT_ID,
                type: "slot",
            },
            {
                id: IVENTIS_SITEMAP_TEXT_SLOT_ID,
                type: "slot",
            },
        ],
        sprite: "",
        fog: {},
    };
}

/** Removes a source from the map once the map is idle */
export function removeSourceOnceMapIsIdle(map: Map, sourceId: string) {
    // Check source exists
    if (map.getSource(sourceId)) {
        // If map is idle remove straight away
        if (map.idle()) {
            map.removeSource(sourceId);
        } else {
            // If not idle, then wait until idle
            map.once("idle", () => {
                if (map.getSource(sourceId)) {
                    map.removeSource(sourceId);
                }
            });
        }
    }
}
